import { CART_CONFIG, getCartItemCount } from '../config/configCart';

/**
 * Pure helper functions for shopping cart manipulation.
 * All functions return new objects (immutable updates) and never mutate input.
 */

/**
 * Add or increment a listing in the cart for a given seller.
 *
 * @param {Object} cart - The full cart object from user privateData
 * @param {string} authorId - The seller's user ID
 * @param {string} listingId - The listing ID to add/increment
 * @param {number} increment - Number to add (positive) or remove (negative)
 * @param {number} maxItems - Max items per seller (defaults to CART_CONFIG)
 * @returns {Object} Updated cart object
 * @throws {Error} If adding would exceed maxItems limit
 */
export const addToCart = (
  cart = {},
  authorId,
  listingId,
  increment = 1,
  maxItems = CART_CONFIG.maxItemsPerSeller
) => {
  const sellerCart = cart[authorId] || {};
  const currentEntry = sellerCart[listingId] || { count: 0 };
  const newCount = Math.max(0, currentEntry.count + increment);

  // Calculate what the new total would be for this seller
  const currentTotal = getCartItemCount(sellerCart);
  const diff = newCount - currentEntry.count;
  const newTotal = currentTotal + diff;

  if (newTotal > maxItems) {
    const error = new Error(
      `Cart limit reached: max ${maxItems} books per seller. Check out first, then start a new order.`
    );
    error.code = 'cart/max-items-exceeded';
    throw error;
  }

  // If count drops to 0, remove the listing from this seller's cart
  if (newCount <= 0) {
    return removeFromCart(cart, authorId, listingId);
  }

  return {
    ...cart,
    [authorId]: {
      ...sellerCart,
      [listingId]: { count: newCount },
    },
  };
};

/**
 * Remove a listing entirely from a seller's cart.
 * If the seller has no more items, removes the seller key too.
 *
 * @param {Object} cart - The full cart object
 * @param {string} authorId - The seller's user ID
 * @param {string} listingId - The listing ID to remove
 * @returns {Object} Updated cart object
 */
export const removeFromCart = (cart = {}, authorId, listingId) => {
  const sellerCart = cart[authorId];
  if (!sellerCart || !sellerCart[listingId]) return cart;

  const { [listingId]: _removed, ...remainingItems } = sellerCart;

  // Check if seller has any listing entries left (excluding deliveryMethod)
  const hasListings = Object.keys(remainingItems).some(key => key !== 'deliveryMethod');

  if (!hasListings) {
    const { [authorId]: _removedSeller, ...remainingCart } = cart;
    return remainingCart;
  }

  return {
    ...cart,
    [authorId]: remainingItems,
  };
};

/**
 * Set the delivery method for a seller's cart.
 *
 * @param {Object} cart - The full cart object
 * @param {string} authorId - The seller's user ID
 * @param {string} method - The delivery method string
 * @returns {Object} Updated cart object
 */
export const setDeliveryMethod = (cart = {}, authorId, method) => {
  const sellerCart = cart[authorId];
  if (!sellerCart) return cart;

  return {
    ...cart,
    [authorId]: {
      ...sellerCart,
      deliveryMethod: method,
    },
  };
};

/**
 * Remove an entire seller's cart (e.g. after successful checkout).
 *
 * @param {Object} cart - The full cart object
 * @param {string} authorId - The seller's user ID to remove
 * @returns {Object} Updated cart object
 */
export const clearSellerCart = (cart = {}, authorId) => {
  const { [authorId]: _removed, ...remainingCart } = cart;
  return remainingCart;
};

/**
 * Get the total number of items across all sellers in the cart.
 * Used for the cart badge count in the topbar.
 *
 * @param {Object} cart - The full cart object
 * @returns {number} Total item count
 */
export const getCartTotalItemCount = (cart = {}) => {
  return Object.keys(cart).reduce((total, authorId) => {
    return total + getCartItemCount(cart[authorId]);
  }, 0);
};

/**
 * Get all seller IDs that have items in the cart.
 *
 * @param {Object} cart - The full cart object
 * @returns {string[]} Array of author ID strings
 */
export const getSellerIds = (cart = {}) => {
  return Object.keys(cart).filter(authorId => getCartItemCount(cart[authorId]) > 0);
};
